import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { formatPhoneNumber } from '@/utils/phoneFormat';
import { z } from 'zod';

const taxIDSchema = z.object({
  formType: z.string().default('Obtain a U.S. Tax ID Number'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phone: z.string().min(1, 'Phone number is required'),
  email: z.string().email('Please enter a valid email address'),
  currentCountry: z.string().min(1, 'Current country is required'),
  passportCountry: z.string().min(1, 'Please select passport country or no passport option'),
  purposeOfITIN: z.string().min(1, 'Please select the purpose for your ITIN'),
  hasSSN: z.string().min(1, 'Please select an option'),
});

type TaxIDFormData = z.infer<typeof taxIDSchema>;

interface TaxIDFormProps {
  onSuccess?: () => void;
}

const TaxIDForm = ({ onSuccess }: TaxIDFormProps = {}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState<TaxIDFormData>({
    formType: 'Obtain a U.S. Tax ID Number',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    currentCountry: '',
    passportCountry: '',
    purposeOfITIN: '',
    hasSSN: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof TaxIDFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const countries = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia',
    'Austria', 'Azerbaijan', 'Bahrain', 'Bangladesh', 'Belarus', 'Belgium', 'Belize', 'Benin',
    'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria',
    'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde', 'Chad', 'Chile',
    'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus',
    'Czech Republic', 'Denmark', 'Djibouti', 'Dominican Republic', 'Ecuador', 'Egypt',
    'El Salvador', 'Estonia', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia',
    'Georgia', 'Germany', 'Ghana', 'Greece', 'Guatemala', 'Guinea', 'Guyana', 'Haiti',
    'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
    'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kuwait',
    'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Lithuania',
    'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Mauritania',
    'Mauritius', 'Mexico', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique',
    'Myanmar', 'Namibia', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria',
    'North Korea', 'Norway', 'Oman', 'Pakistan', 'Panama', 'Papua New Guinea', 'Paraguay',
    'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda',
    'Saudi Arabia', 'Senegal', 'Serbia', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
    'Somalia', 'South Africa', 'South Korea', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname',
    'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Togo',
    'Tunisia', 'Turkey', 'Turkmenistan', 'Uganda', 'Ukraine', 'United Arab Emirates',
    'United Kingdom', 'Uruguay', 'Uzbekistan', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
  ];

  const passportCountries = [
    "I don't have a passport",
    ...countries
  ];

  const purposes = [
    'Banking (Open U.S. Bank Account)',
    'Investment/Financial Services',
    'Tax Filing Requirements',
    'Credit Card Application',
    'PayPal/Stripe Account Setup',
    'Business Operations in U.S.',
    'Real Estate Investment',
    'Other'
  ];

  const handleInputChange = (field: keyof TaxIDFormData, value: string) => {
    // Format phone number as user types
    if (field === 'phone') {
      value = formatPhoneNumber(value);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Close any open dropdowns
    document.body.click();

    try {
      const validatedData = taxIDSchema.parse(formData);
      setErrors({});
      
      // Send data to Zapier webhook
      const response = await fetch('https://hooks.zapier.com/hooks/catch/21776725/u1pdpuq/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'no-cors',
        body: JSON.stringify({
          ...validatedData,
          fullName: `${validatedData.firstName} ${validatedData.lastName}`,
          timestamp: new Date().toISOString(),
          source: window.location.origin
        }),
      });

      // Close the form first if onSuccess callback is provided
      if (onSuccess) {
        onSuccess();
      }

      // Small delay to let the form close before navigation
      setTimeout(() => {
        // Navigate to donation page after successful submission
        navigate('/tax-id-payment');
      }, 300);

      // Reset form after successful submission
      handleReset();
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof TaxIDFormData, string>> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as keyof TaxIDFormData] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast({
          title: "Error",
          description: "Failed to submit your request. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      formType: 'Obtain a U.S. Tax ID Number',
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      currentCountry: '',
      passportCountry: '',
      purposeOfITIN: '',
      hasSSN: '',
    });
    setErrors({});
  };

  return (
    <div className="max-w-2xl -mx-2 md:mx-auto">
      <Card className="w-full bg-background border-gold/20">
        <CardHeader>
          <CardTitle className="font-rockwell text-2xl text-primary">
            U.S. Tax ID Number (ITIN) Application
          </CardTitle>
          <CardDescription>
            Please provide your information to begin the ITIN application process.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleFormSubmit} className="space-y-6">
            <div className="flex flex-col md:grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={errors.firstName ? 'border-destructive' : ''}
                />
                {errors.firstName && (
                  <p className="text-sm text-destructive">{errors.firstName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={errors.lastName ? 'border-destructive' : ''}
                />
                {errors.lastName && (
                  <p className="text-sm text-destructive">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={errors.phone ? 'border-destructive' : ''}
              />
              {errors.phone && (
                <p className="text-sm text-destructive">{errors.phone}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={errors.email ? 'border-destructive' : ''}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="currentCountry">Current Country of Residence *</Label>
              <Select onValueChange={(value) => handleInputChange('currentCountry', value)}>
                <SelectTrigger className={errors.currentCountry ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent className="bg-background border-gold/20 z-50 max-h-60">
                  {countries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.currentCountry && (
                <p className="text-sm text-destructive">{errors.currentCountry}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="passportCountry">Country of Your Current Passport *</Label>
              <Select onValueChange={(value) => handleInputChange('passportCountry', value)}>
                <SelectTrigger className={errors.passportCountry ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select passport country or no passport" />
                </SelectTrigger>
                <SelectContent className="bg-background border-gold/20 z-50 max-h-60">
                  {passportCountries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.passportCountry && (
                <p className="text-sm text-destructive">{errors.passportCountry}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="purposeOfITIN">Primary Purpose for ITIN *</Label>
              <Select onValueChange={(value) => handleInputChange('purposeOfITIN', value)}>
                <SelectTrigger className={errors.purposeOfITIN ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select purpose" />
                </SelectTrigger>
                <SelectContent className="bg-background border-gold/20 z-50">
                  {purposes.map((purpose) => (
                    <SelectItem key={purpose} value={purpose}>
                      {purpose}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.purposeOfITIN && (
                <p className="text-sm text-destructive">{errors.purposeOfITIN}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="hasSSN">Do you currently have a U.S. Social Security Number? *</Label>
              <Select onValueChange={(value) => handleInputChange('hasSSN', value)}>
                <SelectTrigger className={errors.hasSSN ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent className="bg-background border-gold/20 z-50">
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
              {errors.hasSSN && (
                <p className="text-sm text-destructive">{errors.hasSSN}</p>
              )}
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                className="flex-1"
              >
                Reset
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? 'Processing...' : 'Next'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaxIDForm;